import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import { CalendarDays, CameraOff, Loader2, MapPin } from 'lucide-react'
import BrandLogo from '../components/BrandLogo'
import LanguageToggle from '../components/LanguageToggle'
import GuestMediaUpload from '../components/GuestMediaUpload'
import AppFooter from '../components/AppFooter'
import { useLanguage } from '../contexts/LanguageContext'
import { getApiUrl } from '../lib/utils'

const API = getApiUrl()

type PublicEvent = {
  title: string
  date: string
  time: string
  location: string
  language: string
  host_name: string
  accent_color: string | null
  media_upload: boolean
  media_max_image_bytes: number
  media_max_video_bytes: number
}

/**
 * /invite/:inviteLink/share — the standalone guest upload page for the
 * R2-backed media vault. This is the link hosts print on table cards / QR
 * codes: no login, mobile-first, one job.
 */
const MediaUploadPage = () => {
  const { inviteLink } = useParams()
  const { language } = useLanguage()

  const [event, setEvent] = useState<PublicEvent | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    axios
      .get(`${API}/public/event/${inviteLink}`)
      .then((res) => setEvent(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [inviteLink])

  const lang = event?.language || language || 'en'
  const tr = (en: string, ka: string) => (lang === 'ka' ? ka : en)
  const accent = event?.accent_color || '#ff4d8d'

  return (
    <div className="min-h-screen bg-celebrate flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-4">
        <BrandLogo to="/" />
        <LanguageToggle />
      </header>

      <main className="flex-1 w-full max-w-lg mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex justify-center pt-24">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: accent }} />
          </div>
        ) : notFound || !event ? (
          <div className="glass-panel rounded-2xl p-8 mt-12 text-center">
            <h1 className="text-xl font-semibold font-display text-[#1b1033] dark:text-white mb-2">
              {tr('Event not found', 'ღონისძიება ვერ მოიძებნა')}
            </h1>
            <p className="text-sm text-[#4a3d66] dark:text-[#d9d0ec]">
              {tr('Check the link with your host.', 'გადაამოწმეთ ბმული მასპინძელთან.')}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mt-4 mb-6">
              <p className="text-sm font-medium text-[#4a3d66] dark:text-[#d9d0ec]">
                {tr('You are sharing memories with', 'თქვენ უზიარებთ მოგონებებს')}
              </p>
              <h1 className="text-3xl font-semibold font-display tracking-tight text-[#1b1033] dark:text-white mt-1">
                {event.title}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2 text-sm text-[#4a3d66] dark:text-[#d9d0ec]">
                {event.date && (
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4" style={{ color: accent }} />
                    {event.date}
                    {event.time ? ` · ${event.time}` : ''}
                  </span>
                )}
                {event.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" style={{ color: accent }} />
                    {event.location}
                  </span>
                )}
              </div>
            </div>

            {event.media_upload ? (
              <GuestMediaUpload
                inviteLink={inviteLink!}
                theme={{ cardBg: '', textColor: 'text-[#1b1033] dark:text-white', accent }}
                lang={lang}
                caps={{
                  maxImageBytes: event.media_max_image_bytes,
                  maxVideoBytes: event.media_max_video_bytes,
                }}
              />
            ) : (
              <div className="glass-panel rounded-2xl p-8 text-center" data-testid="media-sharing-off">
                <CameraOff className="w-8 h-8 mx-auto mb-3 text-[#4a3d66]/60 dark:text-[#d9d0ec]/60" />
                <h2 className="text-lg font-semibold font-display text-[#1b1033] dark:text-white mb-1">
                  {tr('Photo sharing is off', 'ფოტოების გაზიარება გამორთულია')}
                </h2>
                <p className="text-sm text-[#4a3d66] dark:text-[#d9d0ec]">
                  {tr(
                    'The hosts have not opened the album for uploads yet.',
                    'მასპინძლებს ალბომი ატვირთვებისთვის ჯერ არ გაუხსნიათ.',
                  )}
                </p>
              </div>
            )}

            <p className="text-center mt-6">
              <Link
                to={`/invite/${inviteLink}`}
                className="text-sm font-medium underline-offset-4 hover:underline"
                style={{ color: accent }}
              >
                {tr('View the invitation', 'მოწვევის ნახვა')}
              </Link>
            </p>
          </>
        )}
      </main>

      <AppFooter />
    </div>
  )
}

export default MediaUploadPage
